import { defineStore } from 'pinia';
import { CHANNEL, SUBSCRIPTION } from '@apps-once/definitions';
import { useOnceStore } from '@apps-once/store';
import { useCartStore } from '@apps-once/store/cart';
import { useAddOnStore } from '@apps-once/store/add-on';
import { usePlansStore } from '@apps-billing-shared/store/plans';

export enum CheckoutSteps {
  SELECT_PLAN_DETAILS = 0,
  ADD_CHECKOUT_DETAILS = 1,
  SELECT_ADD_ON_DETAILS = 2,
}

export type SubscriptionPlan = number;

// The most recent initializeCheckout()'s load. Consumers (e.g. the autoPopulate
// AIO gate) await this so they never read business/cart data before it's loaded.
// Defaults resolved so an await before any checkout entry doesn't hang.
let checkoutReady: Promise<void> = Promise.resolve();

// Advances on every checkout entry. A consumer that awaits readiness captures
// this first and re-reads it afterwards: if it moved, a different checkout now
// owns the page and the stale result must be discarded. Callback arrival order
// can't tell them apart, because a new entry reassigns checkoutReady without
// any callback firing (GROWTH-3550).
let checkoutGeneration = 0;

// The resolved default above means an await before any entry is a no-op rather
// than a hang. That is only safe while the classic caller emits
// once:business:checkout before the autoPopulate callback — if that order ever
// changes, the gate would silently stop gating, so say so loudly in dev.
let hasInitializedCheckout = false;

export function whenCheckoutReady(): Promise<void> {
  if (!hasInitializedCheckout && process.env.NODE_ENV !== 'production') {
    console.warn(
      'GROWTH-3550: checkout readiness awaited before initializeCheckout() ran — ' +
        'the gate resolves immediately and is not protecting anything. Check the ' +
        'once:business:checkout / autoPopulate ordering.'
    );
  }
  return checkoutReady;
}

export function currentCheckoutGeneration(): number {
  return checkoutGeneration;
}

type CheckoutStoreState = {
  checkoutStep: number;
  isAioBundle: boolean;
  // True when the checkout was reached from the standalone "Switch to annual"
  // command (which shows its own CHANGE_BILLING dissolution modal), so the
  // inline bundle retained/lost indicators are suppressed here to avoid a
  // double warning. Only the Choose Plan modal entry shows them.
  suppressBundleRetention: boolean;
  bundleUpsellSeen: boolean;
};

export const useCheckoutStore = defineStore('once:checkout', {
  state(): CheckoutStoreState {
    return {
      checkoutStep: CheckoutSteps.SELECT_PLAN_DETAILS,
      isAioBundle: false,
      suppressBundleRetention: false,
      bundleUpsellSeen: false,
    };
  },
  getters: {
    currentCheckoutStep(state: CheckoutStoreState): number {
      return state.checkoutStep;
    },
    isSubscriptionStep(state: CheckoutStoreState): boolean {
      return state.checkoutStep === CheckoutSteps.SELECT_PLAN_DETAILS;
    },
    isAioBundleFlow(state: CheckoutStoreState): boolean {
      return state.isAioBundle;
    },
    isCheckoutDetailsStep(state: CheckoutStoreState): boolean {
      return state.checkoutStep === CheckoutSteps.ADD_CHECKOUT_DETAILS;
    },
    isAddOnPurchaseStep(state: CheckoutStoreState): boolean {
      return state.checkoutStep === CheckoutSteps.SELECT_ADD_ON_DETAILS;
    },
    // Promo audience for this checkout. Existing = the tenant already has a paid
    // subscription (adding an add-on, changing plan, or switching billing
    // schedule); everyone else is net-new / trial conversion. The server
    // re-derives audience from isPaid() as the authoritative gate — this only
    // drives client auto-apply and banners.
    promoAudience(): 'new' | 'existing' {
      return this.isAddOnPurchaseStep ||
        this.isChangePlanFlow ||
        this.isSwitchBillingScheduleFlow
        ? 'existing'
        : 'new';
    },
    // AIO-bundle context: the net-new bundle checkout sets isAioBundleFlow; an
    // existing bundle customer is flagged on the once store (svc-dir). Promo
    // codes for the bundle's products (Payroll/HR/base) must not apply/show here.
    isAioBundleCheckout(): boolean {
      const onceStore = useOnceStore();
      return this.isAioBundleFlow || Boolean(onceStore.isAioBundle);
    },
    isSelectingPlanType(state: CheckoutStoreState): boolean {
      return (
        state.checkoutStep === CheckoutSteps.SELECT_PLAN_DETAILS ||
        state.checkoutStep === CheckoutSteps.ADD_CHECKOUT_DETAILS
      );
    },
    isChangePlanFlow(state: CheckoutStoreState): boolean {
      const onceStore = useOnceStore();

      return (
        onceStore.plan !== SUBSCRIPTION.PRO_TRIAL &&
        onceStore.plan !== SUBSCRIPTION.FREE &&
        onceStore.plan !== SUBSCRIPTION.MOBILE_ONLY &&
        onceStore.plan !== SUBSCRIPTION.FREEMIUM &&
        !this.isAppleToDeputyFlow &&
        state.checkoutStep === CheckoutSteps.SELECT_PLAN_DETAILS
      );
    },
    isTrialConvertFlow(): boolean {
      const onceStore = useOnceStore();
      return (
        onceStore.plan === SUBSCRIPTION.PRO_TRIAL ||
        onceStore.plan === SUBSCRIPTION.FREE ||
        onceStore.plan === SUBSCRIPTION.FREEMIUM ||
        this.isAppleToDeputyFlow
      );
    },
    isAppleToDeputyFlow(): boolean {
      const onceStore = useOnceStore();
      return (
        onceStore.channel === CHANNEL.APPSTORE_APPLE &&
        onceStore.getFeatureFlag('wo-2826-apple-to-deputy-self-serve')
      );
    },
    isAnnualToMonthlyAddOnFlow(): boolean {
      const addOnStore = useAddOnStore();
      return addOnStore.status.isMoveFromAnnualToMonthly;
    },
    isMonthlyToAnnualAddOnFlow(): boolean {
      const addOnStore = useAddOnStore();
      return addOnStore.status.isMoveFromMonthlyToAnnual;
    },
    isSwitchBillingScheduleFlow(): boolean {
      if (this.isAddOnPurchaseStep) {
        return false;
      }
      const relatedPlans = usePlansStore().getRelatedPlans(useCartStore().plan);
      return relatedPlans?.includes(useOnceStore().plan) || false;
    },
    isSwitchBillingScheduleToMonthlyFlow(): boolean {
      return (
        this.isSwitchBillingScheduleFlow &&
        usePlansStore().isMonthly(useCartStore().initialCartPlan)
      );
    },
    shouldHideAddOns(state: CheckoutStoreState): boolean {
      const onceStore = useOnceStore();
      return (
        !onceStore.isFreePlan &&
        onceStore.channel !== CHANNEL.APPSTORE_APPLE &&
        onceStore.channel !== CHANNEL.APPSTORE_GOOGLE &&
        state.checkoutStep === CheckoutSteps.SELECT_PLAN_DETAILS
      );
    },
  },
  actions: {
    async initializeCheckout(id: string): Promise<void> {
      checkoutGeneration += 1;
      hasInitializedCheckout = true;
      checkoutReady = (async (): Promise<void> => {
        await useOnceStore().loadBusiness(id);
        useCartStore().setInitialState();
      })();
      return checkoutReady;
    },
    setCheckoutStep(step: CheckoutSteps): void {
      this.checkoutStep = step;
    },
    incrementCheckoutStep(): void {
      if (this.checkoutStep < Object.keys(CheckoutSteps).length / 2 - 1) {
        this.checkoutStep++;
      }
    },
    decrementCheckoutStep(): void {
      if (this.checkoutStep > 0) {
        this.checkoutStep--;
      }
    },
    reset(): void {
      this.checkoutStep = CheckoutSteps.SELECT_PLAN_DETAILS;
      this.isAioBundle = false;
      this.suppressBundleRetention = false;
      this.bundleUpsellSeen = false;
    },
  },
});
